import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the DepartmentMasterContext
const DepartmentMasterContext = createContext();

// Custom hook to use the DepartmentMasterContext
export const useDepartment = () => useContext(DepartmentMasterContext);

// Provider component to wrap the app
export const DepartmentMasterProvider = ({ children }) => {
  const [departments, setDepartments] = useState([]);
  const API_URL = "https://hrmsnew.prepstripe.com/api/masters/departments"; // Replace with your actual API URL
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDepartments = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(API_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // setDepartments(response.data?.data || {}); // Access `data.data` for actual departments master
      const { data, current_page, last_page, per_page, total } = response.data;

      setDepartments(data || []); // Update leave types list

      setPagination({
          currentPage: current_page,
          totalPages: last_page,
          totalItems: total,
          perPage: per_page,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching departments master:", error);
      setError(error.response?.data?.message || "Failed to fetch departments.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch departments master from the API on load
  useEffect(() => {
    fetchDepartments();
  }, [API_URL, token]);

  // Add a new department
  const addDepartment = async (newDepartment) => {
    try {
      const response = await axios.post(API_URL, newDepartment, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // setDepartments((prev) => [...prev, response.data?.data]);
      setDepartments((prev) => [response.data?.data, ...prev]);
    } catch (error) {
      console.error("Error adding department:", error);
    }
  };

  // Update an existing department
  const updateDepartment = async (updatedDepartment) => {
    try {
      const response = await axios.put(`${API_URL}/${updatedDepartment.id}`, updatedDepartment, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setDepartments((prev) =>
        prev.map((department) =>
          department.id === updatedDepartment.id ? { ...department, ...response.data?.data } : department
        )
      );
    } catch (error) {
      console.error("Error updating department master:", error);
    }
  };

  // Delete a department
  const deleteDepartment = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setDepartments((prev) => prev.filter((department) => department.id !== id));
    } catch (error) {
      console.error("Error deleting department:", error);
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      isLoading,
      error,
      pagination,
      departments,
      addDepartment,
      updateDepartment,
      deleteDepartment,
    }),
    [departments, isLoading] // Recalculate the value only when `departments` changes
  );

  return <DepartmentMasterContext.Provider value={value}>{children}</DepartmentMasterContext.Provider>;
};
